import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ApplicationState } from '@/state/root.models'
import useForm from '@/hooks/form/use-form.hook'
import {
  FormStatusType,
  BonusModeType,
  BonusWageringOrderType,
  BonusRolloverAmountType,
  BonusTriggerType,
  BonusType,
} from '@/enums'

import { authSelector } from '@/state/auth/slices/index.slices'
import { StateSchema } from '@/hooks/form/models'
import { rolloverSettinsSchema } from '../components/schema'

import { rolloverSettingsValidator } from '../components/validator'

type Props = {
  triggerBy: number
  bonusType: number
}

const useRolloverSettings = ({ triggerBy, bonusType }: Props) => {
  const { t } = useTranslation()
  const { currentBonus, bonusFormMode } = useSelector(
    ({ bonusEngine }: ApplicationState) => bonusEngine,
  )

  const { brand } = useSelector(authSelector)

  const { state, handleOnChange, disable, setFormValues, setDisable } = useForm(
    rolloverSettinsSchema(brand.currencies),
    rolloverSettingsValidator(brand.currencies),
    () => {},
  )

  const setCurrentRolloverSettings = () => {
    let schema: StateSchema = {}
    let currencyFlag = true

    brand.currencies.forEach((currency) => {
      const currencyIndex =
        currentBonus.wageringOrder?.rmMinAmount &&
        currentBonus.wageringOrder?.rmMinAmount?.find(
          (index) => index.currency === currency,
        )
      if (currencyIndex) {
        schema = {
          ...schema,
          [`rmMinAmount_${currency}`]: {
            value: currencyIndex.rmMinAmount.toString(),
            error: '',
          },
        }
      } else {
        currencyFlag =
          currentBonus.mode === BonusModeType.NON_STICKY &&
          currentBonus.wageringOrder?.order === BonusWageringOrderType.RM_FIRST
            ? false // eslint-disable-line
            : true
        schema = {
          ...schema,
          [`rmMinAmount_${currency}`]: {
            value: '',
            error:
              currentBonus.mode === BonusModeType.NON_STICKY &&
              currentBonus.wageringOrder?.order ===
                BonusWageringOrderType.RM_FIRST
                ? t('error.requiredField')
                : '',
          },
        }
      }
    })

    setFormValues({
      mode: {
        value: currentBonus.mode,
        error: '',
      },
      rolloverCoefficcient: {
        value: currentBonus.rolloverCoefficcient,
        error: '',
      },
      rollover: {
        value: currentBonus.rollover?.toString(),
        error: '',
      },
      order: {
        value: currentBonus.wageringOrder?.order,
        error: '',
      },
      ...schema,
    })

    bonusFormMode === FormStatusType.CREATE || !currencyFlag
      ? setDisable(true)
      : setDisable(false)
  }

  useEffect(() => {
    let schema: StateSchema = {}

    if (
      state.mode.value === BonusModeType.DEPOSIT_BONUS ||
      state.order.value === BonusWageringOrderType.ANY_ORDER
    ) {
      brand.currencies.forEach((currency) => {
        const currencyIndex =
          currentBonus.wageringOrder?.rmMinAmount &&
          currentBonus.wageringOrder?.rmMinAmount?.find(
            (index) => index.currency === currency,
          )
        if (currencyIndex) {
          schema = {
            ...schema,
            [`rmMinAmount_${currency}`]: {
              value: currencyIndex.rmMinAmount,
              error: '',
            },
          }
        } else {
          schema = {
            ...schema,
            [`rmMinAmount_${currency}`]: {
              value: '',
              error: '',
            },
          }
        }
      })

      setFormValues({
        ...state,
        ...schema,
        order: {
          value:
            state.mode.value === BonusModeType.DEPOSIT_BONUS
              ? BonusWageringOrderType.ANY_ORDER
              : state.order.value,
          error: '',
        },
      })
    }
  }, [state.mode.value, state.order.value]) // eslint-disable-line

  useEffect(() => {
    if (
      triggerBy === BonusTriggerType.BY_BUNDLE ||
      triggerBy === BonusTriggerType.BY_CODE
      // && bonusType.toString() !== BonusType.FREE_PLAY
    ) {
      setFormValues({
        ...state,
        mode: {
          value: BonusModeType.NON_STICKY,
          error: '',
        },
        rolloverCoefficcient: {
          value: BonusRolloverAmountType.GRANTED_BONUS,
          error: '',
        },
      })
    }
  }, [triggerBy]) // eslint-disable-line

  return {
    rolloverSettings: {
      state,
      handleOnChange,
      disable,
      setFormValues,
      setDisable,
    },
    setCurrentRolloverSettings,
  }
}

export default useRolloverSettings
